/**
 * The shared-map page's numbers. A "map" share carries the owner's plan and their now on the share's
 * day, and is forecast here, in the visitor's browser, from that day ("As of"); a "template" share
 * carries only the indexed preview below (today's net worth = 100, income = 100), so no amount ever
 * reaches the page.
 */
import {
  applyChanges, contract, core, debt as debtNs, forecast, formatDay, parseDay, withTimeframe,
  type Checkpoint, type Day, type Decimal, type Plan, type Transaction,
} from '@map-my-money/engine';
import type { TemplateItem, TemplatePreview, WirePlan } from '../api/types.ts';
import { t, type MessageKey } from '../i18n/index.ts';
import { calendarMonths, withTypedDebts, type ShareAnchor } from '@map-my-money/views';
import { decode, linePoints, num } from './model.ts';
import { toWireChanges } from './scenario.ts';

export const HORIZON_YEARS = 20;
export const RANGES = [5, 10, 20] as const;

export interface MapMonth {
  readonly date: Day;
  readonly netWorth: number;
  readonly cash: number;
  readonly investments: number;
  /** Property and other assets at their full value. */
  readonly home: number;
  /** Mortgages and loans still owed, as a positive number. */
  readonly owed: number;
}

export interface MapFlow {
  readonly date: Day;
  readonly income: number;
  readonly spending: number;
  /** Mortgage and loan payments; null when the share folds them into spending (templates). */
  readonly debt: number | null;
  readonly invested: number;
}

export type MilestoneKind = 'goal' | 'mortgageFree' | 'debtFree' | 'retire';

export interface MapMilestone {
  readonly key: string;
  readonly kind: MilestoneKind;
  readonly series: string;
  readonly label: string;
  readonly date: Day;
}

/** Every goal on the shared line, over the one 50-year goal horizon (R26), not only those inside the chart. */
export interface MapGoal {
  readonly id: string;
  readonly label: string;
  readonly series: string;
  /** Today when met on the share's day, else the month's last day; null when not within 50 years. */
  readonly date: Day | null;
  readonly reached: boolean;
  readonly progressPercent: number;
}

export interface MapModel {
  readonly unit: 'money' | 'index';
  readonly asOf: Day;
  readonly horizonYears: number;
  readonly months: readonly MapMonth[];
  readonly flows: readonly MapFlow[];
  readonly milestones: readonly MapMilestone[];
  /** Absent on template shares, which carry no amounts. */
  readonly goals?: readonly MapGoal[];
}

export interface Adjustments {
  readonly spendLess: number;
  readonly investMore: number;
  readonly overpay: number;
}

export const NO_ADJUSTMENTS: Adjustments = { spendLess: 0, investMore: 0, overpay: 0 };
export const isAdjusted = (a: Adjustments) => a.spendLess > 0 || a.investMore > 0 || a.overpay > 0;

const propertyIds = (plan: Plan) =>
  new Set([...plan.assets.filter((a) => a.assetClass === 'property').map((a) => a.id), ...plan.properties.map((p) => p.id)]);

const isMortgage = (tx: Transaction, properties: ReadonlySet<string>) =>
  tx.type === 'debt' && tx.isActive && tx.linkedAssetId !== null && properties.has(tx.linkedAssetId);

/** The plan's largest mortgage (what "overpay the mortgage" overpays), if it has one. */
export function mainMortgage(plan: Plan, asOf: Day): Transaction | null {
  const properties = propertyIds(plan);
  const owed = (tx: Transaction) => num(debtNs.remainingBalance(tx, asOf, asOf));
  return plan.transactions.filter((tx) => isMortgage(tx, properties) && owed(tx) > 0).sort((a, b) => owed(b) - owed(a))[0] ?? null;
}

const GOAL_KIND: Record<string, MilestoneKind> = { debt: 'debtFree', passive: 'retire' };

/** The sharer's now on the share's day, frozen into a map share: it anchors the visitor's line so it starts where theirs did (R19). */
export type { ShareAnchor };

const anchorCheckpoint = (anchor: ShareAnchor, asOf: Day): Checkpoint => {
  const balances = contract.decodeBalances({ cash: anchor.cash, investments: anchor.investments });
  return { id: 'share-anchor', date: asOf, confirmedBalances: balances, forecastedBalances: balances };
};

/** The line over the share's horizon from now (the share's day), split as the page stacks it. */
export function mapModel(plan: Plan, asOf: Day, years = HORIZON_YEARS, anchor: ShareAnchor | null = null): MapModel {
  const day = formatDay(asOf);
  const anchored = anchor?.debts ? withTypedDebts(plan, [{ id: 'share-anchor', date: day, balances: { cash: anchor.cash, investments: anchor.investments }, debts: anchor.debts }], day) : plan;
  const horizon = withTimeframe(anchored, years);
  const f = forecast(horizon, { asOf, checkpoint: anchor === null ? null : anchorCheckpoint(anchor, asOf) });
  const properties = propertyIds(plan);
  const mortgages = plan.transactions.filter((tx) => isMortgage(tx, properties));
  const months: MapMonth[] = linePoints({ forecast: f, today: asOf }).map((p) => {
    const mortgage = mortgages.reduce((total, tx) => total + num(debtNs.remainingBalance(tx, p.date, asOf)), 0);
    const debt = num(p.debt);
    return {
      date: p.date,
      netWorth: num(p.netWorth),
      cash: num(p.cash),
      investments: num(p.investments),
      home: num(p.equity) + mortgage,
      owed: Math.max(0, mortgage + debt),
    };
  });

  const first = core.addMonths(core.startOfMonth(asOf), 1);
  const flows: MapFlow[] = [];
  for (let m = 0; m < 12; m++) {
    const start = core.addMonths(first, m);
    const at = (series: readonly { start: Day; amount: Decimal }[]) => num(series.find((b) => b.start === start)?.amount ?? core.ZERO);
    const p = f.cashflow.payments;
    flows.push({ date: core.addDays(core.addMonths(start, 1), -1), income: at(p.income), spending: at(p.expense), debt: at(p.debt), invested: at(p.investment) });
  }

  const end = months[months.length - 1]?.date ?? asOf;
  const milestones: MapMilestone[] = f.goals
    .filter((g) => !g.isAchieved && g.estimatedDate !== null && g.estimatedDate <= end)
    .map((g) => ({ key: `goal-${g.goal.id}`, kind: GOAL_KIND[g.goal.series] ?? 'goal', series: g.goal.series, label: g.goal.label, date: g.estimatedDate! }));
  if (mortgages.length > 0 && (months[0]?.owed ?? 0) > 0) {
    const clear = months.find((m, i) => i > 0 && mortgageLeft(mortgages, m.date, asOf) <= 0.5);
    if (clear) milestones.push({ key: 'mortgage-free', kind: 'mortgageFree', series: 'debt', label: t('smap.milestone.mortgageFree'), date: clear.date });
  }
  milestones.sort((a, b) => a.date - b.date);
  const goals = f.goals.map((g) => ({
    id: g.goal.id, label: g.goal.label, series: g.goal.series, date: g.estimatedDate, reached: g.isAchieved, progressPercent: g.progressPercent,
  }));
  return { unit: 'money', asOf, horizonYears: years, months, flows, milestones, goals };
}

const mortgageLeft = (mortgages: readonly Transaction[], date: Day, asOf: Day) =>
  mortgages.reduce((total, tx) => total + num(debtNs.remainingBalance(tx, date, asOf)), 0);

/**
 * The visitor's changes on top of the shared plan: spending cuts and extra investing are added
 * as the What If screen adds them; an overpayment re-enters the main mortgage at today's
 * balance with the higher payment, so only payments from now on change.
 */
export function adjustedPlan(wire: WirePlan, plan: Plan, asOf: Day, adjustments: Adjustments): Plan {
  let base = plan;
  const mortgage = adjustments.overpay > 0 ? mainMortgage(plan, asOf) : null;
  if (mortgage) {
    const balance = debtNs.remainingBalance(mortgage, asOf, asOf);
    const overpaid: Transaction = {
      ...mortgage,
      debtEntryMode: 'currentBalance',
      debtCurrentBalance: core.round2(balance),
      amount: core.add(mortgage.amount, core.fromDouble(adjustments.overpay)),
      endDate: null,
      endAfterOccurrences: null,
      debtRemainingMonths: null,
    };
    base = { ...plan, transactions: plan.transactions.map((tx) => (tx.id === mortgage.id ? overpaid : tx)) };
  }
  const additions = toWireChanges(
    [
      { id: 'map-spend', kind: 'spendLess', amount: adjustments.spendLess },
      { id: 'map-invest', kind: 'invest', amount: adjustments.investMore },
    ],
    wire,
    asOf,
  );
  return applyChanges(base, { transactions: additions.transactions.map(contract.decodeTransaction) });
}

export function sharedMapModel(wire: WirePlan, asOf: Day, adjustments: Adjustments = NO_ADJUSTMENTS, anchor: ShareAnchor | null = null): MapModel {
  const plan = decode(wire);
  return mapModel(isAdjusted(adjustments) ? adjustedPlan(wire, plan, asOf, adjustments) : plan, asOf, HORIZON_YEARS, anchor);
}

// MARK: Templates

const round1 = (v: number) => Math.round(v * 10) / 10;
const monthKey = (day: Day) => formatDay(day).slice(0, 7);
const dayFromMonth = (month: string) => parseDay(`${month}-01`);

const GENERIC_GOAL: Record<string, MessageKey> = {
  netWorth: 'share.goal.netWorth', investment: 'share.goal.investment', cash: 'share.goal.cash', debt: 'share.goal.debt', passive: 'share.goal.passive',
};

/** What a template share publishes: the forecast's shape, indexed, with generic milestone names. */
export function templatePreview(plan: Plan, asOf: Day): TemplatePreview {
  const model = mapModel(plan, asOf);
  const base = Math.abs(model.months[0]?.netWorth ?? 0) || 1;
  const index = (v: number) => round1((v / base) * 100);
  const incomeBase = model.flows.reduce((total, f) => total + f.income, 0) / Math.max(1, model.flows.length) || 1;
  const flowIndex = (v: number) => round1((v / incomeBase) * 100);
  return {
    timeframeYears: model.horizonYears,
    months: model.months.map((m) => ({
      month: monthKey(m.date), netWorth: index(m.netWorth), cash: index(m.cash), investments: index(m.investments), home: index(m.home), mortgage: index(m.owed),
    })),
    cashFlow: model.flows.map((f) => ({
      month: monthKey(f.date), income: flowIndex(f.income), spending: flowIndex(f.spending + (f.debt ?? 0)), invested: flowIndex(f.invested),
    })),
    milestones: model.milestones.map((m) => ({
      key: m.kind === 'mortgageFree' ? 'mortgage-free' : `${m.series}-${m.key}`,
      label: m.kind === 'mortgageFree' ? t('smap.milestone.mortgageFree') : t(GENERIC_GOAL[m.series] ?? 'share.goal.netWorth'),
      date: formatDay(m.date),
    })),
  };
}

export function templateStructure(wire: WirePlan): TemplateItem[] {
  const transactions = (wire.transactions ?? []).filter((tx) => tx.isActive !== false).map((tx): TemplateItem => ({
    label: tx.label ?? tx.id, type: tx.type, frequency: tx.frequency ?? 'monthly', startDate: tx.startDate ?? null,
    paymentDayOfMonth: tx.paymentDayOfMonth ?? null, kind: 'transaction',
  }));
  const assets = (wire.assets ?? []).filter((a) => a.isActive !== false).map((a): TemplateItem => ({
    label: a.label ?? a.id, type: a.assetClass ?? 'asset', frequency: null, startDate: a.purchaseDate ?? null, paymentDayOfMonth: null, kind: 'asset',
  }));
  return [...transactions, ...assets];
}

// Labels in a template are the owner's words; anything that still reads as money is replaced.
const MONEY_IN_TEXT = /[£$€¥₹]\s?\d|\b\d[\d,.]*\s?[kKmMbB]\b|\b\d{1,3}(?:,\d{3})+\b|\b\d{4,}\b(?!-\d)/;
export const safeLabel = (label: string, fallback: string) => (MONEY_IN_TEXT.test(label) && !/^\d{4}$/.test(label.trim()) ? fallback : label);

const MILESTONE_KIND = (key: string): { kind: MilestoneKind; series: string } => {
  if (key.startsWith('mortgage-free')) return { kind: 'mortgageFree', series: 'debt' };
  const series = key.split('-')[0] ?? 'netWorth';
  return { kind: GOAL_KIND[series] ?? 'goal', series };
};

export function templateModel(preview: TemplatePreview, asOf: string): MapModel {
  return {
    unit: 'index',
    asOf: parseDay(asOf),
    horizonYears: preview.timeframeYears,
    // The first point is now, on the share's day; each later one is the end of its month.
    months: preview.months.map((m, i) => ({
      date: i === 0 ? parseDay(asOf) : core.addDays(core.addMonths(dayFromMonth(m.month), 1), -1), netWorth: m.netWorth, cash: m.cash, investments: m.investments, home: m.home, owed: Math.abs(m.mortgage),
    })),
    flows: preview.cashFlow.slice(0, 12).map((f) => ({ date: dayFromMonth(f.month), income: f.income, spending: f.spending, debt: null, invested: f.invested })),
    milestones: preview.milestones
      .map((m) => {
        const { kind, series } = MILESTONE_KIND(m.key);
        return { key: m.key, kind, series, label: safeLabel(m.label, t(GENERIC_GOAL[series] ?? 'share.goal.netWorth')), date: parseDay(m.date) };
      })
      .sort((a, b) => a.date - b.date),
  };
}

// MARK: Derived figures

/** The first point on or after `date`: a month's end, or now itself. */
export const indexAt = (model: Pick<MapModel, 'months'>, date: Day): number => {
  const i = model.months.findIndex((m) => m.date >= date);
  return i === -1 ? model.months.length - 1 : i;
};

/** The month end `years` after the share's day (the line starts at now, then this month's end). */
export const monthIndex = (model: MapModel, years: number): number => indexAt(model, core.addMonths(model.asOf, years * 12));

export const monthAt = (model: MapModel, years: number): MapMonth | undefined => model.months[monthIndex(model, years)];

export interface MapDelta {
  readonly netWorth10: number;
  readonly mortgageFree: { readonly date: Day | null; readonly months: number | null } | null;
  readonly biggest: { readonly milestone: MapMilestone; readonly months: number } | null;
}

/** How the visitor's version differs from the shared one (months > 0 = sooner). */
export function mapDelta(shared: MapModel, yours: MapModel): MapDelta {
  const moves = milestoneMoves(shared, yours);
  const mortgage = shared.milestones.find((m) => m.kind === 'mortgageFree');
  const biggest = [...moves.values()].filter((m) => m.months !== null && m.months !== 0).sort((a, b) => Math.abs(b.months!) - Math.abs(a.months!))[0];
  return {
    netWorth10: (monthAt(yours, 10)?.netWorth ?? 0) - (monthAt(shared, 10)?.netWorth ?? 0),
    mortgageFree: mortgage ? { date: yours.milestones.find((m) => m.key === mortgage.key)?.date ?? null, months: moves.get(mortgage.key)?.months ?? null } : null,
    biggest: biggest ? { milestone: biggest.milestone, months: biggest.months! } : null,
  };
}

export interface MilestoneMove {
  readonly milestone: MapMilestone;
  /** Months sooner than the shared map (negative = later); null when it moved out of view. */
  readonly months: number | null;
}

export function milestoneMoves(shared: MapModel, yours: MapModel): Map<string, MilestoneMove> {
  const out = new Map<string, MilestoneMove>();
  for (const before of shared.milestones) {
    const after = yours.milestones.find((m) => m.key === before.key);
    out.set(before.key, { milestone: after ?? before, months: after ? calendarMonths(after.date, before.date) : null });
  }
  return out;
}
